'use client'

import {
  canEditClaim,
  getDisabledClaimTooltip,
  getDisabledStatusesFromPermissions,
  getDisabledStatusesFromCurrentStatuses,
  mergeDisabledStatuses
} from '@repo/utils/claimPermissions'
import { Avatar } from '@repo/ui/Avatar'
import { Chat } from '@repo/ui/Chat'
import { confirm } from '@repo/ui/Confirm'
import { ClaimStatusDropdownButton } from '@repo/ui/ClaimStatusDropdownButton'
import { ClientNameAndMatterDescription } from '@repo/ui/ClientNameAndMatterDescription'
import { EditIcon } from '@repo/ui/EditIcon'
import { formatCurrency } from '@repo/utils/numbers'
import { convertDateToLocalTimeInIsoFormat, formatDate, getRelativeDate, parseDate } from '@repo/utils/time'
import { FileAttachmentIcon } from '@repo/ui/FileAttachmentIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { Pill } from '@repo/ui/Pill'
import { PostingDateDialog } from '@repo/ui/PostingDateDialog'
import { ArrowRightIcon } from '@repo/ui/ArrowRightIcon'
import { useAuditHistoryStore } from '@repo/store/useAuditHistoryStore'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useClaimsStore } from '@repo/store/useClaimsStore'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCommentsStore } from '@repo/store/useCommentsStore'
import { useExpensesStore } from '@repo/store/useExpensesStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIIconButton from '@mui/material/IconButton'
import MUIDivider from '@mui/material/Divider'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

export const ExpensesReviewModule = () => {
  const {
    fetchExpenseDetails,
    fetchExpenseClaimLocationByExpenseClaimId,
    updateExpenseClaimStatuses,
    expensesTableUpdate,
    fetchExpenseClaimPeriodStart,
    expenseClaimPeriodStart
  } = useExpensesStore()
  const { fetchExpenseClaimAuditHistoryByExpenseClaimId } = useAuditHistoryStore()
  const { userId } = useAuthenticationStore()
  const {
    comments,
    createExpensesClaimComment,
    downloadExpenseClaimReceipt,
    updateExpensesClaimComment,
    deleteExpensesClaimComment,
    fetchAllExpensesClaimCommentsByExpenseClaimId
  } = useCommentsStore()
  const { isClaimsDrawerOpen, setIsClaimsDrawerOpen, fetchAllExpenseUserPermissionsByProcessorUserId } =
    useClaimsStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { allowedActions } = useMenuItemsStore()
  const { submitExpenseClaims, processExpenseClaims } = allowedActions
  const { fetchMatter } = useMattersStore()
  const navigate = useNavigate()
  const theme = useTheme()
  const location = useLocation()

  const [matterDetails, setMatterDetails] = useState<any>(null)
  const [expenseDetails, setExpenseDetails] = useState<any>(null)
  const [expenseLocations, setExpenseLocations] = useState<any[]>([])
  const [claimStatus, setClaimStatus] = useState<number | null>(location.state?.status ?? null)
  const [claimStatusText, setClaimStatusText] = useState<string>('')
  const [auditHistory, setAuditHistory] = useState<any[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [receiptFilename, setReceiptFilename] = useState<string>('')
  const [permissions, setPermissions] = useState<any[]>([])
  const [permissionsLoading, setPermissionsLoading] = useState(true)
  const [postingDateDialogOpen, setPostingDateDialogOpen] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<number | null>(null)
  const [isSettingPostingDate, setIsSettingPostingDate] = useState(false)
  const historyScrollRef = useRef<HTMLDivElement>(null)

  const expenseClaimId = location.pathname.split('/').pop() || null
  const isEditDisabled = !canEditClaim(false, claimStatus)

  useEffect(() => {
    if (!userId) return

    let cancelled = false
    setPermissionsLoading(true)

    fetchExpenseClaimPeriodStart()

    fetchAllExpenseUserPermissionsByProcessorUserId({ processorUserId: userId })
      .then((result: any) => {
        if (!cancelled) setPermissions(Array.isArray(result) ? result : [])
      })
      .catch((error: any) => {
        console.error('Error fetching expense user permissions:', error)
        if (!cancelled) setPermissions([])
      })
      .finally(() => {
        if (!cancelled) setPermissionsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [userId, fetchAllExpenseUserPermissionsByProcessorUserId, fetchExpenseClaimPeriodStart])

  const { disabledStatuses, tooltips: statusTooltips } = useMemo(() => {
    let permResult
    if (permissionsLoading || !expenseDetails?.claimRecipientUserId) {
      permResult = getDisabledStatusesFromPermissions([], '')
    } else if (permissions.length === 0) {
      const disabledStatuses = [0]
      const tooltips: Record<number, string> = { 0: 'Claims cannot be changed to draft status' }

      if (!submitExpenseClaims?.allowed) {
        ;[1, 2, 3, 4].forEach((s) => {
          disabledStatuses.push(s)
          tooltips[s] = 'You do not have permission to approve claims'
        })
      }

      if (!processExpenseClaims?.allowed) {
        ;[5, 6, 7, 8].forEach((s) => {
          disabledStatuses.push(s)
          tooltips[s] = 'You do not have permission to process payments'
        })
      }

      permResult = { disabledStatuses, tooltips }
    } else {
      permResult = getDisabledStatusesFromPermissions(permissions, expenseDetails.claimRecipientUserId)
    }

    const statusResult =
      claimStatus !== null && claimStatus !== undefined
        ? getDisabledStatusesFromCurrentStatuses([claimStatus])
        : { disabledStatuses: [] as number[], tooltips: {} as Record<number, string> }

    return mergeDisabledStatuses(permResult, statusResult)
  }, [permissions, permissionsLoading, expenseDetails?.claimRecipientUserId, claimStatus, submitExpenseClaims?.allowed, processExpenseClaims?.allowed])

  useEffect(() => {
    if (!expenseClaimId) return

    const getExpenseDetails = async () => {
      try {
        const [response, locationsResponse] = await Promise.all([
          fetchExpenseDetails({ expenseClaimId }),
          fetchExpenseClaimLocationByExpenseClaimId({ expenseClaimId })
        ])
        setExpenseDetails(response)
        if (response?.status !== undefined) setClaimStatus(response.status)
        setExpenseLocations(Array.isArray(locationsResponse) ? locationsResponse : [])
        if (response?.receiptFilename) setReceiptFilename(response.receiptFilename)
      } catch (error) {
        console.error('Error fetching expense details:', error)
      }
    }

    getExpenseDetails()
  }, [expenseClaimId, fetchExpenseDetails, fetchExpenseClaimLocationByExpenseClaimId, expensesTableUpdate])

  useEffect(() => {
    if (!expenseDetails?.clientNo || !expenseDetails?.matterNo) return

    const clientMatterNo = `${expenseDetails.clientNo}-${expenseDetails.matterNo}`
    fetchMatter({ clientMatterNo }).then((matter: any) => {
      if (matter) setMatterDetails(matter)
    })
  }, [expenseDetails?.clientNo, expenseDetails?.matterNo, fetchMatter])

  useEffect(() => {
    if (!expenseClaimId) return

    setCommentsLoading(true)
    fetchAllExpensesClaimCommentsByExpenseClaimId({ expenseClaimId }).finally(() => {
      setCommentsLoading(false)
    })
  }, [expenseClaimId, fetchAllExpensesClaimCommentsByExpenseClaimId])

  useEffect(() => {
    if (isClaimsDrawerOpen || !expenseClaimId) return

    setCommentsLoading(true)
    fetchAllExpensesClaimCommentsByExpenseClaimId({ expenseClaimId }).finally(() => {
      setCommentsLoading(false)
    })
    fetchExpenseDetails({ expenseClaimId }).then((response: any) => {
      if (response) {
        setExpenseDetails(response)
        if (response.status !== undefined) setClaimStatus(response.status)
        if (response.receiptFilename) setReceiptFilename(response.receiptFilename)
      }
    })
  }, [isClaimsDrawerOpen])

  useEffect(() => {
    if (!expenseClaimId) return

    const fetchHistory = async () => {
      setHistoryLoading(true)
      try {
        const response = await fetchExpenseClaimAuditHistoryByExpenseClaimId({ expenseClaimId })
        setAuditHistory(response)
      } catch (error) {
        console.error('Error fetching audit history:', error)
      } finally {
        setHistoryLoading(false)
      }
    }

    fetchHistory()
  }, [expenseClaimId, fetchExpenseClaimAuditHistoryByExpenseClaimId])

  useEffect(() => {
    if (!historyLoading && auditHistory.length > 0 && historyScrollRef.current) {
      historyScrollRef.current.scrollTop = historyScrollRef.current.scrollHeight
    }
  }, [historyLoading, auditHistory])

  const mappedComments = (comments || []).map((comment: any) => ({
    id: comment.expenseClaimCommentId,
    message: comment.comment,
    senderUsername: comment.createdBy,
    timestamp: comment.createdDate
  }))

  const handleSendComment = async (message: string) => {
    if (!expenseClaimId) return
    const now = new Date().toISOString()
    await createExpensesClaimComment({
      expenseClaimId,
      comment: message,
      createdDate: now,
      createdBy: userId,
      updatedDate: now,
      updatedBy: userId
    })
    await fetchAllExpensesClaimCommentsByExpenseClaimId({ expenseClaimId })
  }

  const handleEditComment = async (messageId: string | number, newMessage: string) => {
    const now = new Date().toISOString()
    await updateExpensesClaimComment({
      expenseClaimCommentId: messageId,
      expenseClaimId,
      comment: newMessage,
      updatedDate: now,
      updatedBy: userId
    })
    if (expenseClaimId) {
      await fetchAllExpensesClaimCommentsByExpenseClaimId({ expenseClaimId })
    }
  }

  const handleDeleteComment = async (messageId: string | number) => {
    const proceed = await confirm('Are you sure you want to delete this comment?')
    if (!proceed) return

    await deleteExpensesClaimComment({ expenseClaimCommentId: messageId })
    if (expenseClaimId) {
      await fetchAllExpensesClaimCommentsByExpenseClaimId({ expenseClaimId })
    }
  }

  useEffect(() => {
    switch (claimStatus) {
      case 0:
        setClaimStatusText('This claim is currently in draft status. It has not yet been submitted for review.')
        break
      case 1:
        setClaimStatusText('This claim is currently pending review. It has been submitted and is awaiting action.')
        break
      case 2:
        setClaimStatusText('This claim has been approved. It has been reviewed and accepted.')
        break
      case 3:
        setClaimStatusText('This claim has been rejected. It has been reviewed and declined.')
        break
      case 4:
        setClaimStatusText(
          'This claim has action required. It has been reviewed but requires additional information or correction before it can proceed.'
        )
        break
      case 5:
        setClaimStatusText(
          'This claim is ready to pay. It has been reviewed and approved, and is now awaiting payment processing.'
        )
        break
      case 6:
        setClaimStatusText(
          'This claim has been posted. It has been processed and recorded in the system, but payment has not yet been issued.'
        )
        break
      case 7:
        setClaimStatusText(
          'This claim has been paid. It has been processed and payment has been issued to the claimant.'
        )
        break
      case 8:
        setClaimStatusText(
          'This claim is TOIL approved. It has been reviewed and approved for Time Off in Lieu (TOIL) instead of monetary reimbursement.'
        )
        break
      default:
        setClaimStatusText('Please select a claim status from the dropdown.')
    }
  }, [claimStatus])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleDownloadReceipt = useCallback(async () => {
    if (!expenseClaimId) return
    try {
      const response = await downloadExpenseClaimReceipt({ expenseClaimId })
      if (!response) return

      const blob = response instanceof Blob ? response : new Blob([response])
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = receiptFilename || 'receipt'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading receipt:', error)
    }
  }, [expenseClaimId, downloadExpenseClaimReceipt, receiptFilename])

  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

  const incurredDateValue = expenseDetails?.incurredDate
    ? parseDate(String(expenseDetails.incurredDate).slice(0, 10), 'yyyy-MM-dd')
    : null
  const periodStart = expenseClaimPeriodStart ? startOfDay(new Date(expenseClaimPeriodStart)) : null

  const proceedStatusUpdate = async (newStatus: number, postingDate?: Date) => {
    await updateExpenseClaimStatuses({
      expenseClaimIds: [expenseDetails.expenseClaimId],
      status: newStatus,
      // Sent date-only so a BST/UTC offset can't shift it a day.
      postingDate: postingDate ? convertDateToLocalTimeInIsoFormat(postingDate).slice(0, 10) : null
    })
  }

  const updateStatus = async ({ claimStatus }: any) => {
    try {
      // Moving a claim to "Ready to pay" (5) requires the user to choose a posting date first.
      if (claimStatus === 5 && expenseDetails?.status < 5) {
        setPendingStatus(claimStatus)
        setPostingDateDialogOpen(true)
        return
      }
      await proceedStatusUpdate(claimStatus)
    } catch (error) {
      console.error('Error updating claim status:', error)
    }
  }

  const handleCancelPostingDate = () => {
    setPostingDateDialogOpen(false)
    setPendingStatus(null)
    // Revert the dropdown to the persisted status, since the change was abandoned.
    setClaimStatus(expenseDetails?.status ?? null)
  }

  const handleConfirmPostingDate = async (date: Date) => {
    if (pendingStatus === null) return
    setIsSettingPostingDate(true)
    try {
      await proceedStatusUpdate(pendingStatus, date)
      setPostingDateDialogOpen(false)
      setPendingStatus(null)
    } catch (error) {
      console.error('Error setting posting date:', error)
    } finally {
      setIsSettingPostingDate(false)
    }
  }

  return (
    <MUIStack
      data-testid="ExpensesReviewModule"
      direction="column"
      sx={{
        width: '100%',
        maxWidth: '1600px',
        margin: pageTitleBlockIsVisible === true ? '0 auto 2rem' : '8rem auto 2rem',
        minHeight: '45rem',
        height: 'fit-content',
        padding: '24px 40px 40px'
      }}
    >
      <MUIButton
        variant="text"
        onClick={() => navigate('/accounts/employee-claims/expense-claims')}
        sx={{
          color: 'text.secondary',
          textTransform: 'none',
          mb: 4,
          px: 1,
          width: 'fit-content',
          minWidth: 'auto',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
        }}
      >
        <ArrowRightIcon
          sx={{ transform: 'rotate(180deg)', height: '18px', width: '18px', color: theme.palette.primary.main, mr: 2 }}
        />
        <MUITypography variant="body1" sx={{ mb: 0, fontWeight: 600, pointerEvents: 'none' }}>
          All expense claims
        </MUITypography>
      </MUIButton>
      <MUIStack direction="row" sx={{ gap: '1rem', height: 'fit-content' }}>
        <MUIPaper
          sx={{
            alignItems: 'center',
            background: 'white',
            border: '1px solid #D8DCE5',
            borderRadius: 1,
            boxShadow: 'none',
            flex: 4,
            maxHeight: 'fit-content',
            width: '60%'
          }}
        >
          <MUIStack direction="column" sx={{ m: 4, gap: 2, px: 4 }}>
            <MUIStack direction="row" sx={{ gap: 1, alignItems: 'baseline', justifyContent: 'space-between' }}>
              <MUITypography variant="h6">Claim Details</MUITypography>
              <MUITooltip
                arrow
                placement="top"
                title={isEditDisabled ? getDisabledClaimTooltip('edit') : 'Edit claim details'}
              >
                <span>
                  <MUIButton
                    variant="text"
                    size="small"
                    onClick={() => setIsClaimsDrawerOpen(true, { expenseClaimId: expenseClaimId }, false)}
                    disabled={isEditDisabled}
                    sx={{
                      borderRadius: 4,
                      backgroundColor: theme.palette.grey[200],
                      width: '1rem',
                      height: '1rem',
                      minWidth: '1rem',
                      padding: 4
                    }}
                  >
                    <EditIcon sx={{ height: '1rem', width: '1rem' }} />
                  </MUIButton>
                </span>
              </MUITooltip>
            </MUIStack>
            <MUIDivider />

            <MUIStack direction="row" justifyContent="space-between" gap={10} flexWrap="wrap" sx={{ py: 4 }}>
              <MUIStack direction="column" sx={{ gap: 10, flex: 1, minWidth: '14rem' }}>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Date incurred
                  </MUITypography>
                  <MUITypography variant="body2">{formatDate(expenseDetails?.incurredDate) || '-'}</MUITypography>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Posting date
                  </MUITypography>
                  <MUITypography variant="body2">{formatDate(expenseDetails?.postingDate || expenseDetails?.incurredDate) || '-'}</MUITypography>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Claim recipient
                  </MUITypography>
                  <MUITypography variant="body2">{expenseDetails?.claimRecipientUserId || '-'}</MUITypography>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Associated matter
                  </MUITypography>
                  {expenseDetails?.clientNo ? (
                    <MUIStack
                      direction="row"
                      alignItems="center"
                      sx={{ gap: 1, maxWidth: '20rem', mt: 2, backgroundColor: '#F8F9FB', borderRadius: 1, p: 2 }}
                    >
                      <Avatar
                        name={
                          matterDetails?.forename && matterDetails?.surname
                            ? `${matterDetails.forename} ${matterDetails.surname}`
                            : matterDetails?.surname || matterDetails?.clientName || ''
                        }
                        sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
                      />
                      <ClientNameAndMatterDescription
                        clientNo={expenseDetails.clientNo}
                        matterNo={Number(expenseDetails.matterNo)}
                        surname={
                          matterDetails?.forename && matterDetails?.surname
                            ? `${matterDetails.forename} ${matterDetails.surname}`
                            : matterDetails?.surname || matterDetails?.clientName || ''
                        }
                        matterDescription={matterDetails?.matterDescription || ''}
                        highlightIfHighRisk={true}
                      />
                    </MUIStack>
                  ) : (
                    <MUITypography variant="body2">-</MUITypography>
                  )}
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Claim description
                  </MUITypography>
                  <MUITypography variant="body2">{expenseDetails?.description || '-'}</MUITypography>
                </MUIStack>
              </MUIStack>

              <MUIStack direction="column" sx={{ gap: 10, flex: 1, minWidth: '14rem' }}>
                <MUIStack direction="row" alignItems="center" justifyContent="space-between">
                  <MUIStack direction="column">
                    <MUITypography
                      variant="body1"
                      sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                    >
                      Amount
                    </MUITypography>
                    <MUITypography variant="body2">{formatCurrency(expenseDetails?.grossAmount) || '-'}</MUITypography>
                  </MUIStack>
                  {expenseDetails?.isRecoverable === true && (
                    <Pill
                      label="Recoverable"
                      color={'#3D517B'}
                      backgroundColor={'transparent'}
                      sx={{
                        height: '25px',
                        border: '1px solid #3D517B',
                        borderRadius: '16px',
                        [`& span`]: { fontSize: '0.75rem' }
                      }}
                    />
                  )}
                </MUIStack>
                <MUIStack direction="column">
                  <MUIStack direction="row" alignItems="center" justifyContent="space-between">
                    <MUIStack direction="column">
                      <MUITypography
                        variant="body1"
                        sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                      >
                        Net
                      </MUITypography>
                      <MUITypography variant="body2">{formatCurrency(expenseDetails?.netAmount) || '-'}</MUITypography>
                    </MUIStack>
                    <MUIStack direction="column">
                      <MUITypography
                        variant="body1"
                        sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                      >
                        Vat
                      </MUITypography>
                      <MUITypography variant="body2">{formatCurrency(expenseDetails?.vatAmount) || '-'}</MUITypography>
                    </MUIStack>
                    {expenseDetails?.isVatApplicable === true && (
                      <Pill
                        label="VAT-applicable"
                        color={'#3D517B'}
                        backgroundColor={'transparent'}
                        sx={{
                          height: '25px',
                          border: '1px solid #3D517B',
                          borderRadius: '16px',
                          [`& span`]: { fontSize: '0.75rem' }
                        }}
                      />
                    )}
                  </MUIStack>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Claim type
                  </MUITypography>
                  <MUITypography variant="body2">
                    {expenseDetails?.expenseClaimTypeDescription || expenseDetails?.expenseClaimTypeId || '-'}
                  </MUITypography>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Locations
                  </MUITypography>
                  {expenseLocations.length > 0 ? (
                    expenseLocations.map((loc: any, index: number) => (
                      <MUITypography key={loc.expenseClaimLocationId || index} variant="body2">
                        {[loc.house, loc.area, loc.postalTown, loc.county, loc.postcode].filter(Boolean).join(', ') ||
                          '-'}
                      </MUITypography>
                    ))
                  ) : (
                    <MUITypography variant="body2">-</MUITypography>
                  )}
                </MUIStack>
                <MUIStack direction="column">
                  <MUIStack direction="row" justifyContent="space-between">
                    <MUIStack direction="column">
                      <MUITypography
                        variant="body1"
                        sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                      >
                        Total miles
                      </MUITypography>
                      <MUITypography variant="body2">{expenseDetails?.miles || '-'}</MUITypography>
                    </MUIStack>
                    {expenseDetails?.journeyType === 'O' ? (
                      <Pill
                        label="One-way journey"
                        color={'#3D517B'}
                        backgroundColor={'transparent'}
                        sx={{
                          height: '25px',
                          border: '1px solid #3D517B',
                          borderRadius: '16px',
                          [`& span`]: { fontSize: '0.75rem' }
                        }}
                      />
                    ) : expenseDetails?.journeyType === 'R' ? (
                      <Pill
                        label="Return journey"
                        color={'#3D517B'}
                        backgroundColor={'transparent'}
                        sx={{
                          height: '25px',
                          border: '1px solid #3D517B',
                          borderRadius: '16px',
                          [`& span`]: { fontSize: '0.75rem' }
                        }}
                      />
                    ) : null}
                  </MUIStack>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 1 }}
                  >
                    Claim Receipt
                  </MUITypography>
                  {expenseDetails?.receiptFilename ? (
                    <MUIStack
                      direction="row"
                      alignItems="center"
                      sx={{
                        backgroundColor: '#F8F9FB',
                        borderRadius: 1,
                        p: 1.5,
                        gap: 2
                      }}
                    >
                      <FileAttachmentIcon sx={{ height: '24px', width: '24px', mx: '4px' }} />
                      <MUIStack direction="column" sx={{ flex: 1, minWidth: 0 }}>
                        <MUITypography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {receiptFilename || 'Receipt'}
                        </MUITypography>
                        <MUITypography variant="caption" color="text.secondary">
                          {expenseDetails?.receiptFileSize ? formatFileSize(expenseDetails.receiptFileSize) : ''}
                        </MUITypography>
                      </MUIStack>
                      <MUIIconButton
                        onClick={handleDownloadReceipt}
                        size="small"
                        sx={{ color: '#5073C3', flexShrink: 0 }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M12 8V16M12 16L8 12M12 16L16 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </MUIIconButton>
                    </MUIStack>
                  ) : (
                    <MUITypography variant="body2" color="text.secondary">
                      No receipt attached
                    </MUITypography>
                  )}
                </MUIStack>
              </MUIStack>
            </MUIStack>
          </MUIStack>
        </MUIPaper>

        <MUIStack direction="column" sx={{ flex: 3, gap: '1rem', maxWidth: '30rem' }}>
          <MUIPaper
            sx={{
              background: 'white',
              border: '1px solid #D8DCE5',
              borderRadius: 1,
              boxShadow: 'none'
            }}
          >
            <ClaimStatusDropdownButton
              value={claimStatus}
              onChange={(newStatus) => {
                setClaimStatus(newStatus)
                updateStatus({ claimStatus: newStatus })
              }}
              isExpensesTable={true}
              disabledStatuses={disabledStatuses}
              statusTooltips={statusTooltips}
              sx={{ width: '100%', m: 4, maxWidth: '-webkit-fill-available', height: '3rem' }}
            />
            <MUITypography variant="body2" sx={{ color: '#6B778C', m: 4 }}>
              {claimStatusText}
            </MUITypography>
          </MUIPaper>

          <MUIPaper
            sx={{
              background: 'white',
              border: '1px solid #D8DCE5',
              borderRadius: 1,
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 'fit-content',
              flex: 1
            }}
          >
            <MUITypography variant="h6" sx={{ m: 4, mb: 0 }}>
              Approval notes
            </MUITypography>
            {commentsLoading ? (
              <LoadingState sx={{ flex: 1 }} />
            ) : (
              <Chat
                messages={mappedComments}
                sendHandler={handleSendComment}
                onEditMessage={handleEditComment}
                onDeleteMessage={handleDeleteComment}
              />
            )}
          </MUIPaper>

          <MUIPaper
            sx={{
              background: 'white',
              border: '1px solid #D8DCE5',
              borderRadius: 1,
              boxShadow: 'none',
              overflow: 'auto'
            }}
          >
            <MUITypography variant="h6" sx={{ m: 4, mb: 2, minHeight: 'fit-content', flex: 1 }}>
              History
            </MUITypography>
            {historyLoading ? (
              <LoadingState sx={{ height: '100%' }} />
            ) : (
              <MUIStack ref={historyScrollRef} spacing={2} sx={{ px: 4, pb: 4, maxHeight: 300, overflow: 'auto' }}>
                {(auditHistory || []).map((item: any, index: number) => (
                  <MUIBox key={index}>
                    <MUITypography variant="body2" sx={{ mb: 0 }}>
                      {item.description}
                    </MUITypography>
                    <MUITypography variant="caption" color="text.secondary">
                      {getRelativeDate(item.createdDate)}
                    </MUITypography>
                  </MUIBox>
                ))}
              </MUIStack>
            )}
          </MUIPaper>
        </MUIStack>
      </MUIStack>

      <PostingDateDialog
        open={postingDateDialogOpen}
        onClose={handleCancelPostingDate}
        onConfirm={handleConfirmPostingDate}
        periodStart={periodStart}
        incurredDate={incurredDateValue}
        isInClosedPeriod={expenseDetails?.isInClosedPeriod}
        isSaving={isSettingPostingDate}
      />
    </MUIStack>
  )
}
